import {__} from "@wordpress/i18n";

/**
 * The DXP_REST_API variable defines the base URL for the DXPToolkit REST API.
 *
 * @type {string}
 * @constant
 */
export const DXP_REST_API = window.CFCCAdmin.restURL;
export const REST_NONCE = window.CFCCAdmin.nonce;
export const BROWSE_RULES = [
	{
		title: "Popular Rules",
		wide: true,
		rules: [
			{
				id: "NewUser",
				name: __("New visitor", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to visitors that are new to your website.",
			},
			{
				id: "DynamicLink",
				multi: true,
				name: __("Dynamic link", "dxp"),
				typeID: "WebsiteBehavior",
				details:
					"This rule applies to visitors that are on a page with a dynamic link, such as UTM parameters commonly used in marketing campaigns. <br> Learn more about UTM parameters & dynamic links <a href='https://blog.hubspot.com/marketing/what-are-utm-tracking-codes-ht' target='_blank' rel='nofollow' title='What are UTM parameters?'>here</a>.",
			},
			{
				id: "Returning2",
				name: __("2nd visit of returning user", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to visitors that are returning to your website for the second time.",
			},
			{
				id: "CommonReferrers",
				multi: true,
				name: __("Common referral website", "dxp"),
				typeID: "BrowserBehavior",
				premium: true,
				details:
				"This rule applies to visitors that were referred to you by a common website, such as Google or Facebook.",
			},
		],
	},
	{
		title: "Device Type",
		rules: [
			{
				id: "MobileDevice",
				name: __("Visitor is on a mobile device", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that are browsing your website from a mobile device, excluding tablets.",
			},
			{
				id: "TabletDevice",
				name: __("Visitor is on a tablet device", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that are browsing your website from a tablet device, excluding laptops & notebooks.",
			},
			{
				id: "DesktopDevice",
				name: __("Visitor is on a desktop device", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that are browsing your website from a desktop device, including laptops & notebooks.",
			},
		],
	},
	{
		title: "Browser Behavior",
		premium: true,
		rules: [
			{
				id: "BrowserLanguage",
				multi: true,
				name: __("Visitor is using a specific browser language", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that have their browser configured in the language you select above.",
			},
			{
				id: "BrowserCookie",
				multi: true,
				name: __("Visitor has a specific browser cookie", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that have a cookie saved in their browser with the name and value you configured above. <br> Learn more about cookies <a href='https://policies.google.com/technologies/cookies' target='_blank' rel='nofollow' title='What are cookies?'>here</a>.",
			},
		],
	},
	{
		title: "User Behavior",
		rules: [
			{
				id: "NewUser",
				name: __("New visitor on your website", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to visitors that are new to your website.",
			},
			{
				id: "Returning2",
				name: __("2nd visit of returning user", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to visitors that are returning to your website for the second time.",
			},
			{
				id: "Returning3",
				name: __("3rd visit of returning user", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to visitors that are returning to your website for the third time.",
			},
			{
				id: "ReturningCustom",
				multi: true,
				name: __("Returning user - custom number of visits", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to visitors that are returning to your website for the number of times you configured above.",
			},
		],
	},
	{
		title: "Integrations",
		premium: true,
		rules: [
			{
				id: "SubmittedGravityForms",
				multi: true,
				name: __("Gravity Forms form submission", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to users who have responded to a Gravity Forms form on your website.",
			},
			{
				id: "ConvertKitCustomField",
				name: __("ConvertKit custom field", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to users who have a specific value in a custom field in ConvertKit.",
			},
			{
				id: "HubspotsProperties",
				name: __("HubSpot contact property", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to users who have a specific value in a property in HubSpot.",
			},
		],
	},
	{
		title: "e-Commerce",
		wide: true,
		premium: true,
		rules: [
			{
				id: "Cart",
				name: __("WooCommerce Cart Not Empty", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "PurchaseOrder",
				name: __("Placed a WooCommerce order", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "CartProduct",
				multi: true,
				name: __("Specific product in cart", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "CartCategory",
				multi: true,
				name: __("Specific category in cart", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "TotalSpent",
				multi: true,
				name: __("User Orders Lifetime Value", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "PurchaseProduct",
				multi: true,
				name: __("Purchased a specific product", "dxp"),
				typeID: "UserBehavior",
				categoryID: "UserBased",
			},
			{
				id: "PurchaseCategory",
				multi: true,
				name: __("Purchased a product from a specific category", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "NumberOfOrders",
				multi: true,
				name: __("# of orders placed", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "NumberOfItemsInCart",
				multi: true,
				name: __("# of items in WooCommerce cart", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "AverageOrderValue",
				multi: true,
				name: __("Average order value", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "ValueOfItemsInCart",
				multi: true,
				name: __("WooCommerce cart value", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "WooCommerceMembership",
				name: __("Has a WooCommerce membership", "dxp"),
				typeID: "UserBehavior",
			},
			{
				id: "WooCommerceSubscription",
				name: __("Has a WooCommerce subscription", "dxp"),
				typeID: "UserBehavior",
			},
		],
	},
	{
		title: "Operating System",
		premium: true,
		rules: [
			{
				id: "macOS",
				name: __("Visitor is on a macOS device", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that are using the macOS operating system.",
			},
			{
				id: "Windows",
				name: __("Visitor is on a Windows device", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that are using the Windows operating system.",
			},
			{
				id: "Linux",
				name: __("Visitor is on a Linux device", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that are using the Linux operating system.",
			},
			{
				id: "iOS",
				name: __("Visitor is on an iOS device", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that are using the iOS operating system.",
			},
			{
				id: "Android",
				name: __("Visitor is on an Android device", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that are using the Android operating system.",
			},
		],
	},
	{
		title: "WordPress User Information",
		premium: true,
		rules: [
			{
				id: "LoggedIn",
				name: __("Visitor is logged in", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to visitors that are logged in to your website.",
			},
			{
				id: "GuestUser",
				name: __("Visitor is a guest (not logged in)", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to visitors that are not logged in to your website.",
			},
			{
				id: "UserRole",
				multi: true,
				name: __("Visitor has a given user role", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to logged-in visitors that have the user role you select above. <br> Learn more about WordPress user roles <a href='https://wordpress.com/support/invite-people/user-roles/' target='_blank' rel='nofollow' title='What are user roles?'>here</a>.",
			},
			{
				id: "UserMeta",
				multi: true,
				name: __("Visitor has a given user meta", "dxp"),
				typeID: "UserBehavior",
				details:
					"This rule applies to logged-in visitors that have the WordPress metadata you select above linked to their account. <br> Learn more about WordPress metadata <a href='https://www.wpbeginner.com/glossary/metadata/' target='_blank' rel='nofollow' title='What is metadata?'>here</a>.",
			},
		],
	},
	{
		title: "User Acquisition",
		rules: [
			{
				id: "CommonReferrers",
				multi: true,
				name: __("Visitor was referred by a common website", "dxp"),
				typeID: "BrowserBehavior",
				premium: true,
				details:
					"This rule applies to visitors that were referred to you by a common website, such as Google or Facebook.",
			},
			{
				id: "PageReferrers",
				multi: true,
				name: __(
					"Visitor was referred by a page on your website",
					"dxp",
				),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that were referred to you by a page on your website.",
			},
			{
				id: "CustomReferrers",
				multi: true,
				name: __("Visitor was referred by a custom website", "dxp"),
				typeID: "BrowserBehavior",
				details:
					"This rule applies to visitors that were referred to you by a custom website.",
			},
		],
	},
	{
		title: "Website Interaction",
		rules: [
			{
				id: "DynamicLink",
				multi: true,
				name: __("User is on a page with a dynamic link", "dxp"),
				typeID: "WebsiteBehavior",
				details:
					"This rule applies to visitors that are on a page with a dynamic link, such as UTM parameters commonly used in marketing campaigns.  <br> Learn more about UTM parameters & dynamic links <a href='https://blog.hubspot.com/marketing/what-are-utm-tracking-codes-ht' target='_blank' rel='nofollow' title='What are UTM parameters?'>here</a>.",
			},
			{
				id: "VisitedPages",
				name: __("User has visited a page on your website", "dxp"),
				typeID: "WebsiteBehavior",
				details:
					"This rule applies to visitors that have previously visited a page on your website.",
			},
		],
	},
	{
		title: "Time & Location",
		rules: [
			{
				id: "DateTime",
				multi: true,
				name: __("User is visiting within a time period", "dxp"),
				typeID: "WebsiteBehavior",
				details:
					"This rule applies to visitors that are visiting your website within the time & date period you select above.",
			},
			{
				id: "DateTimeSchedule",
				name: __("User is visiting during a specific schedule", "dxp"),
				typeID: "WebsiteBehavior",
				premium: true,
				details:
					"This rule applies to visitors that are visiting your website during the schedule you select above.",
			},
			{
				id: "GeoCountry",
				name: __("Visitor is from a specific country", "dxp"),
				typeID: "BrowserBehavior",
				premium: true,
				details:
					"This rule applies to visitors that are browsing your website from the country you select above.",
			},
		],
	},
];

// Rule Types Constants.
export { RULES_TYPES, DEFAULT_RULES } from "@app/config/constants/Rules";
